/* eslint-disable no-console */
/**
 * Pure cart reducer function
 */

import type { CartState, CartAction, CartItemKey } from './cart-types'
import { createCartItemKey } from './cart-types'

/**
 * Pure reducer function for cart state management
 */
export const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { policyId, product, qty = 1 } = action.payload
      const key = createCartItemKey(policyId, product.id)
      const existingItem = state.items[key]

      const isMandatory = product.isMandatory === 1
      if (isMandatory) return state

      return {
        ...state,
        items: {
          ...state.items,
          [key]: {
            policyId,
            productId: product.id,
            qty: existingItem ? existingItem.qty + qty : qty,
            isGreenpoolProduct: false,
            product,
            asset: undefined,
            policy: existingItem?.policy,
          },
        },
      }
    }

    case 'ADD_ITEMS_PER_POLICY': {
      // clear existing items for this policy
      const { policyId, products } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Keep only items that don't belong to this policy
      for (const [key, item] of Object.entries(state.items)) {
        if (item.policyId !== policyId) {
          updatedItems[key] = item
        }
      }

      // Add new items
      products.forEach(product => {
        const key = createCartItemKey(policyId, product.id)
        updatedItems[key] = {
          policyId,
          productId: product.id,
          qty: 1,
          product,
          policy: undefined,
          asset: undefined,
          isGreenpoolProduct: false,
        }
      })

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'REMOVE_ITEM': {
      const { policyId, productId } = action.payload
      const key = createCartItemKey(policyId, productId)
      const { ...remainingItems } = state.items
      delete remainingItems[key]

      return {
        ...state,
        items: remainingItems,
      }
    }

    case 'ADD_GREENPOOL_ITEM': {
      const { policyId, asset } = action.payload
      const key = createCartItemKey(policyId, asset.id)
      const existingItem = state.items[key]
      const { ...remainingItems } = state.items

      // Remove all other greenpool items for this policy (only 1 allowed)
      for (const [key, item] of Object.entries(state.items)) {
        if (item.policyId === policyId && item.isGreenpoolProduct) {
          delete remainingItems[key]
        }
      }

      return {
        ...state,
        items: {
          ...remainingItems,
          [key]: {
            policyId,
            productId: asset.id,
            qty: 1,
            isGreenpoolProduct: true,
            product: undefined,
            asset,
            policy: existingItem?.policy,
          },
        },
      }
    }

    case 'REMOVE_GREENPOOL_ITEM': {
      const { policyId, assetId } = action.payload
      const key = createCartItemKey(policyId, assetId)
      const { ...remainingItems } = state.items
      delete remainingItems[key]

      return {
        ...state,
        items: remainingItems,
      }
    }

    case 'INCREMENT_QTY': {
      const { policyId, productId, delta = 1 } = action.payload
      const key = createCartItemKey(policyId, productId)
      const existingItem = state.items[key]

      if (!existingItem) return state

      return {
        ...state,
        items: {
          ...state.items,
          [key]: {
            ...existingItem,
            qty: existingItem.qty + delta,
          },
        },
      }
    }

    case 'DECREMENT_QTY': {
      const { policyId, productId, delta = 1 } = action.payload
      const key = createCartItemKey(policyId, productId)
      const existingItem = state.items[key]

      if (!existingItem) return state

      const newQty = existingItem.qty - delta

      // Remove item if quantity becomes 0 or negative
      if (newQty <= 0) {
        const { ...remainingItems } = state.items
        delete remainingItems[key]

        return {
          ...state,
          items: remainingItems,
        }
      }

      return {
        ...state,
        items: {
          ...state.items,
          [key]: {
            ...existingItem,
            qty: newQty,
          },
        },
      }
    }

    case 'SET_QTY': {
      const { policyId, productId, qty } = action.payload
      const key = createCartItemKey(policyId, productId)
      const existingItem = state.items[key]

      if (!existingItem) return state

      // Remove item if quantity is 0 or negative
      if (qty <= 0) {
        const { ...remainingItems } = state.items
        delete remainingItems[key]

        return {
          ...state,
          items: remainingItems,
        }
      }

      return {
        ...state,
        items: {
          ...state.items,
          [key]: {
            ...existingItem,
            qty,
          },
        },
      }
    }

    case 'CLEAR_CART': {
      return {
        ...state,
        items: {},
      }
    }

    case 'CLEAR_POLICY': {
      const { policyId } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Keep only items that don't belong to this policy
      for (const [key, item] of Object.entries(state.items)) {
        if (item.policyId !== policyId) {
          updatedItems[key] = item
        }
      }

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'HYDRATE_PRODUCTS': {
      const { productsById } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Update existing items with product details
      for (const [key, item] of Object.entries(state.items)) {
        const product = productsById[item.productId]

        if (product) {
          // Product found - update with fresh data from API
          updatedItems[key] = {
            ...item,
            product,
          }
        } else {
          // Product not found in API response - remove silently
          console.warn(`Product ${item.productId} not found in API response, removing from cart`)
          // Don't add to updatedItems (effectively removes it)
        }
      }

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'HYDRATE_POLICIES': {
      const { policiesById } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Update existing items with policy details
      for (const [key, item] of Object.entries(state.items)) {
        const policy = policiesById[item.policyId]

        updatedItems[key] = {
          ...item,
          policy: policy || item.policy, // Keep existing if not found
        }
      }

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'HYDRATE_ITEMS': {
      const { items } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Merge hydrated items with current state, preserving existing product/policy details
      for (const item of items) {
        const key = createCartItemKey(item.policyId, item.productId)

        updatedItems[key] = {
          policyId: item.policyId,
          productId: item.productId,
          qty: item.qty,
          isGreenpoolProduct: item.isGreenpoolProduct,
          product: item?.product,
          asset: item?.asset,
          policy: item?.policy,
        }
      }

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'SYNC_FROM_STORAGE': {
      const { items: persistedItems } = action.payload
      const updatedItems: Record<CartItemKey, any> = {}

      // Merge persisted items with current state, preserving product/policy details
      for (const persistedItem of persistedItems) {
        const key = createCartItemKey(persistedItem.policyId, persistedItem.productId)
        const currentItem = state.items[key]

        updatedItems[key] = {
          policyId: persistedItem.policyId,
          productId: persistedItem.productId,
          qty: persistedItem.qty,
          isGreenpoolProduct: persistedItem.isGreenpoolProduct,
          product: currentItem?.product, // Preserve loaded product details
          asset: currentItem?.asset, // Preserve loaded asset details for greenpool items
          policy: currentItem?.policy, // Preserve loaded policy details
        }
      }

      return {
        ...state,
        items: updatedItems,
      }
    }

    case 'SET_TERMS_ACCEPTED': {
      const { accepted } = action.payload
      return {
        ...state,
        isTermsAccepted: accepted,
      }
    }

    case 'SET_SELECTED_ADDRESS': {
      const { addressId } = action.payload
      return {
        ...state,
        selectedAddressId: addressId,
      }
    }

    case 'SET_CUSTOM_ADDRESS': {
      const { address, zip, city } = action.payload
      return {
        ...state,
        customAddress: { address, zip, city },
      }
    }

    case 'SET_IS_PRIVATE_ADDRESS': {
      const { isPrivateAddress } = action.payload
      return {
        ...state,
        isPrivateAddress,
      }
    }

    case 'ADD_POLICY_TERMS_STATUS': {
      const { policyId, status } = action.payload

      return {
        ...state,
        policiesTermsStatus: {
          ...state.policiesTermsStatus,
          [policyId]: status,
        },
      }
    }

    case 'CLEAR_CONFIRM_DATA': {
      return {
        ...state,
        selectedAddressId: undefined,
        isTermsAccepted: false,
        customAddress: undefined,
        isPrivateAddress: undefined,
      }
    }

    // reset whole cart when user changes
    case 'RESET_CART_FOR_USER': {
      const { userId } = action.payload
      return {
        ...state,
        items: {},
        isTermsAccepted: false,
        selectedAddressId: undefined,
        customAddress: undefined,
        userId, // bind cart to the new user (or null for guest)
      }
    }

    default: {
      // Exhaustive type checking - ensures all action types are handled
      return state
    }
  }
}
